import * as XLSX from "xlsx";
import DOMPurify from "dompurify";

export function stripHtml(html) {
  if (!html) return "";
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["b", "i", "strong", "em", "ul", "ol", "li", "p", "br"],
    ALLOWED_ATTR: [],
  });
  const div = document.createElement("div");
  div.innerHTML = clean;
  return div.textContent || div.innerText || "";
}

export function buildProducts(rows) {
  const byHandle = new Map();

  rows.forEach((r) => {
    const handle = r["Handle"] || r["handle"] || r["HANDLE"];
    if (!handle) return;

    const title = r["Title"] || r["title"];
    const vendor = r["Vendor"] || r["Brand"] || r["vendor"];
    const type = r["Type"] || r["type"];
    const tags = (r["Tags"] || r["tags"] || "").toString().split(",").map(t => t.trim()).filter(Boolean);
    const bodyHtml = r["Body (HTML)"] || r["Body"] || r["Description"] || "";
    const priceUsd = parseFloat((r["Variant Price"] || r["Price"] || r["Variant Price "] || "").toString().replace(/[^0-9.]/g, "")) || null;
    const compareAtUsd = parseFloat((r["Variant Compare At Price"] || r["Compare At Price"] || "").toString().replace(/[^0-9.]/g, "")) || null;
    const sku = (r["Variant SKU"] || r["SKU"] || "").toString();
    const inStock = (() => {
      const qty = parseInt(r["Variant Inventory Qty"] || r["Inventory"] || 0, 10);
      const policy = (r["Variant Inventory Policy"] || "").toString().toLowerCase();
      if (Number.isFinite(qty)) return qty > 0 || policy === "continue";
      return true;
    })();
    const imageSrc = r["Image Src"] || r["Image"] || r["Image URL"] || "";

    if (!byHandle.has(handle)) {
      byHandle.set(handle, {
        id: handle,
        handle,
        name: title || "Untitled",
        brand: vendor || "Unknown Brand",
        category: type || "Uncategorized",
        descriptionHtml: bodyHtml,
        tags,
        variants: [],
        images: [],
        rating: 4 + Math.round(Math.random() * 10) / 10 - 0.5,
      });
    }

    const p = byHandle.get(handle);
    p.variants.push({ sku, priceUsd, compareAtUsd, inStock });
    if (imageSrc && !p.images.includes(imageSrc)) p.images.push(imageSrc);
    if (bodyHtml && (p.descriptionHtml || "").length < bodyHtml.length) p.descriptionHtml = bodyHtml;
  });

  return Array.from(byHandle.values()).map(p => {
    const priced = p.variants.filter(v => v.priceUsd != null);
    const rep = priced.length ? priced.reduce((min, v) => (v.priceUsd < min.priceUsd ? v : min)) : p.variants[0] || {};
    return {
      ...p,
      priceUsd: rep?.priceUsd ?? null,
      compareAtUsd: rep?.compareAtUsd ?? null,
      inStock: rep?.inStock ?? true,
      sku: rep?.sku ?? "",
    };
  });
}

export async function loadFilesFromPaths(paths) {
  if (!paths || !paths.length) return [];
  const allRows = [];
  
  for (const p of paths) {
    try {
      const res = await fetch(p);
      if (!res.ok) { 
        console.warn("Failed to fetch", p, res.status); 
        continue; 
      }
      
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("text") || p.endsWith(".csv")) {
        const text = await res.text();
        const wb = XLSX.read(text, { type: "string" });
        wb.SheetNames.forEach(s => { 
          const rows = XLSX.utils.sheet_to_json(wb.Sheets[s], { defval: "" }); 
          allRows.push(...rows); 
        });
      } else {
        const buf = await res.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        wb.SheetNames.forEach(s => { 
          const rows = XLSX.utils.sheet_to_json(wb.Sheets[s], { defval: "" }); 
          allRows.push(...rows); 
        });
      }
    } catch (err) {
      console.error("Failed to load", p, err);
    }
  }
  
  return allRows.length ? buildProducts(allRows) : [];
}

export const usdFmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });